import type { RelayIceServer, MediaCallMessage } from "./types";
import { SignalChannel } from "./SignalChannel";

type PeerConnectionSignalingOptions = {
  channel: SignalChannel;
  iceServers: RelayIceServer[];
  /**
   * Perfect-negotiation role. Exactly one side must be polite. The polite peer
   * rolls back its own offer on glare; the impolite peer ignores the incoming
   * offer. We make the callee polite and the caller impolite.
   */
  polite: boolean;
  onStateChange?: (state: RTCPeerConnectionState | RTCIceConnectionState) => void;
  onRemoteStream?: (stream: MediaStream) => void;
};

export class PeerConnectionSignaling {
  private readonly id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  private peerConnection: RTCPeerConnection | null = null;
  private removeHandler: (() => void) | null = null;
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private readonly remoteStream = new MediaStream();

  // Senders are reused for the whole call: mute = replaceTrack(null),
  // unmute/switch = replaceTrack(track). The first track of a kind is added
  // with addTrack, which fires onnegotiationneeded so direction is negotiated
  // for BOTH peers (this is what makes media flow in both directions).
  private audioSender: RTCRtpSender | null = null;
  private videoSender: RTCRtpSender | null = null;

  // Perfect-negotiation guards (W3C example).
  private makingOffer = false;
  private ignoreOffer = false;
  private isSettingRemoteAnswerPending = false;

  constructor(private readonly options: PeerConnectionSignalingOptions) {}

  initialize(): RTCPeerConnection {
    if (this.peerConnection) return this.peerConnection;

    this.options.channel.connect();
    this.removeHandler = this.options.channel.addHandler((message) => {
      void this.handleMessage(message);
    });

    const pc = new RTCPeerConnection({
      iceServers: this.options.iceServers,
      iceTransportPolicy: "all",
      bundlePolicy: "balanced",
    });

    pc.onnegotiationneeded = async () => {
      try {
        this.makingOffer = true;
        await pc.setLocalDescription();
        if (!pc.localDescription) return;
        await this.send({
          type: pc.localDescription.type as "offer" | "answer",
          sdp: pc.localDescription.toJSON(),
        });
      } catch (error) {
        console.error("[MediaCall] negotiation failed", error);
      } finally {
        this.makingOffer = false;
      }
    };

    pc.onicecandidate = ({ candidate }) => {
      if (!candidate) return;
      void this.send({ type: "candidate", candidate: candidate.toJSON() });
    };

    pc.ontrack = (event) => {
      const track = event.track;

      for (const existingTrack of this.remoteStream.getTracks()) {
        if (existingTrack.kind === track.kind && existingTrack.id !== track.id) {
          this.remoteStream.removeTrack(existingTrack);
        }
      }

      if (!this.remoteStream.getTracks().some((item) => item.id === track.id)) {
        this.remoteStream.addTrack(track);
      }

      const handleEnded = () => {
        if (this.remoteStream.getTracks().some((item) => item.id === track.id)) {
          this.remoteStream.removeTrack(track);
        }
        track.removeEventListener("ended", handleEnded);
        this.options.onRemoteStream?.(new MediaStream(this.remoteStream.getTracks()));
      };
      track.addEventListener("ended", handleEnded);

      this.options.onRemoteStream?.(new MediaStream(this.remoteStream.getTracks()));
    };

    pc.onconnectionstatechange = () => {
      this.options.onStateChange?.(pc.connectionState);
    };

    pc.oniceconnectionstatechange = () => {
      this.options.onStateChange?.(pc.iceConnectionState);
    };

    this.peerConnection = pc;
    return pc;
  }

  /**
   * Set (or clear with `null`) the locally sent track for a kind. The first
   * non-null track of a kind is added via addTrack (triggers renegotiation);
   * afterwards it is a plain replaceTrack with no renegotiation.
   */
  async setTrack(kind: "audio" | "video", track: MediaStreamTrack | null): Promise<void> {
    const pc = this.initialize();
    const sender = kind === "audio" ? this.audioSender : this.videoSender;

    if (sender) {
      await sender.replaceTrack(track);
      return;
    }

    if (!track) return;

    const created = pc.addTrack(track);
    if (kind === "audio") this.audioSender = created;
    else this.videoSender = created;
  }

  /** Initial media setup: route a stream's live tracks onto the senders. */
  async replaceStream(stream: MediaStream): Promise<void> {
    this.initialize();
    const audioTrack = stream.getAudioTracks().find((track) => track.readyState === "live") ?? null;
    const videoTrack = stream.getVideoTracks().find((track) => track.readyState === "live") ?? null;
    await this.setTrack("audio", audioTrack);
    if (videoTrack) await this.setTrack("video", videoTrack);
  }

  /** Stop sending a kind without removing the sender (mute) — no renegotiation. */
  async disableTrack(kind: "audio" | "video"): Promise<void> {
    const sender = kind === "audio" ? this.audioSender : this.videoSender;
    await sender?.replaceTrack(null);
  }

  async send(message: Omit<MediaCallMessage, "iam">): Promise<void> {
    await this.options.channel.publish({ ...message, iam: this.id } as MediaCallMessage);
  }

  destroy(): void {
    this.removeHandler?.();
    this.removeHandler = null;

    if (this.peerConnection) {
      for (const sender of this.peerConnection.getSenders()) {
        sender.track?.stop();
      }

      for (const receiver of this.peerConnection.getReceivers()) {
        receiver.track?.stop();
      }

      this.peerConnection.onnegotiationneeded = null;
      this.peerConnection.onicecandidate = null;
      this.peerConnection.ontrack = null;
      this.peerConnection.onconnectionstatechange = null;
      this.peerConnection.oniceconnectionstatechange = null;
      this.peerConnection.close();
      this.peerConnection = null;
    }

    this.audioSender = null;
    this.videoSender = null;
    this.makingOffer = false;
    this.ignoreOffer = false;
    this.isSettingRemoteAnswerPending = false;
    this.pendingCandidates = [];
    for (const track of this.remoteStream.getTracks()) {
      this.remoteStream.removeTrack(track);
    }
  }

  private async handleMessage(message: MediaCallMessage): Promise<void> {
    if (message.iam === this.id) return;
    if (!("type" in message)) return;

    const pc = this.initialize();

    if (message.type === "offer" || message.type === "answer") {
      const description = message.sdp;
      const readyForOffer =
        !this.makingOffer &&
        (pc.signalingState === "stable" || this.isSettingRemoteAnswerPending);
      const offerCollision = description.type === "offer" && !readyForOffer;

      // Impolite peer wins glare by ignoring the incoming offer. Polite peer
      // accepts it — setRemoteDescription performs an implicit rollback, then
      // onnegotiationneeded re-fires so its own pending changes are re-offered.
      this.ignoreOffer = !this.options.polite && offerCollision;
      if (this.ignoreOffer) return;

      this.isSettingRemoteAnswerPending = description.type === "answer";
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(description));
      } finally {
        this.isSettingRemoteAnswerPending = false;
      }
      await this.flushCandidates();

      if (description.type === "offer") {
        await pc.setLocalDescription();
        if (!pc.localDescription) return;
        await this.send({
          type: pc.localDescription.type as "offer" | "answer",
          sdp: pc.localDescription.toJSON(),
        });
      }
      return;
    }

    if (message.type === "candidate") {
      try {
        if (!pc.remoteDescription) {
          this.pendingCandidates.push(message.candidate);
          return;
        }
        await pc.addIceCandidate(new RTCIceCandidate(message.candidate));
      } catch (error) {
        if (!this.ignoreOffer) throw error;
      }
    }
  }

  private async flushCandidates(): Promise<void> {
    const pc = this.peerConnection;
    if (!pc || !pc.remoteDescription) return;

    const candidates = this.pendingCandidates.splice(0);
    for (const candidate of candidates) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (error) {
        if (!this.ignoreOffer) throw error;
      }
    }
  }
}
